import React, { useState, useCallback } from 'react';
import { Handle, Position, useReactFlow } from 'reactflow';
import { Chip, IconButton } from '@mui/material';
import AddRounded from '@mui/icons-material/AddRounded';
import CloseIcon from '@mui/icons-material/Close';
import { ItemSelector } from './ItemSelector';

export const SwitchNode = ({ data, selected, id }) => {
  const { setNodes, setEdges } = useReactFlow();
  const [isSelectorOpen, setIsSelectorOpen] = useState(false);
  const [activeItemIndex, setActiveItemIndex] = useState(null);

  const handleItemSelect = useCallback((item) => {
    if (activeItemIndex !== null) {
      // Обновляем существующий предмет
      setNodes((nds) =>
        nds.map((node) => {
          if (node.id === id) {
            const newItems = [...(node.data.items || [])];
            newItems[activeItemIndex] = { 
              itemId: item.id, 
              itemName: item.name,
              itemCost: item.cost,
              itemDescription: item.description
            };
            return {
              ...node,
              data: {
                ...node.data,
                items: newItems
              }
            };
          }
          return node;
        })
      );
    } else {
      // Добавляем новый предмет
      setNodes((nds) =>
        nds.map((node) => {
          if (node.id === id) {
            return {
              ...node,
              data: {
                ...node.data,
                items: [...(node.data.items || []), { 
                  itemId: item.id, 
                  itemName: item.name,
                  itemCost: item.cost,
                  itemDescription: item.description
                }]
              }
            };
          }
          return node;
        })
      );
    }
    setIsSelectorOpen(false);
    setActiveItemIndex(null);
  }, [id, setNodes, activeItemIndex]);

  const handleItemRemove = useCallback((index) => {
    setNodes((nds) =>
      nds.map((node) => {
        if (node.id === id) {
          const newItems = [...(node.data.items || [])];
          newItems.splice(index, 1);
          return {
            ...node,
            data: {
              ...node.data,
              items: newItems
            }
          };
        }
        return node;
      })
    );

    // Удаляем соответствующее соединение
    setEdges((eds) => 
      eds.filter(edge => !(
        edge.source === id && 
        edge.sourceHandle === `item-${index}`
      ))
    );
  }, [id, setNodes, setEdges]);

  const handleAddItem = useCallback(() => {
    setIsSelectorOpen(true);
    setActiveItemIndex(null);
  }, []);

  const handleEditItem = useCallback((index) => {
    setActiveItemIndex(index);
    setIsSelectorOpen(true);
  }, []);

  return (
    <div className="react-flow__node-default" style={{ 
      position: 'relative',
      minHeight: data.items && data.items.length > 0 ? `${40 + ((data.items.length + 2) * 30)}px` : '100px'
    }}>
      <Handle
        type="target"
        position={Position.Left}
        style={{ 
          left: 0,
          top: 24,
          transform: 'translateY(-50%)'
        }}
      />
      
      <div className="condition-label">
        <label style={{ display: 'block' }}>Item Switch</label>
      </div>

      <div style={{ padding: 20, paddingTop: 0, paddingBottom: 5 }}>
        {selected ? (
          // Режим редактирования - показываем чипы на тех же позициях что и пины
          <>
            {data.items?.map((item, index) => (
              <div key={index} style={{ 
                position: 'absolute',
                right: 25,
                top: `${40 + (index * 30)}px`,
                transform: 'translateY(-50%)',
                display: 'flex',
                alignItems: 'center',
                gap: '2px'
              }}>
                <IconButton
                  size="small"
                  onClick={() => handleItemRemove(index)}
                  sx={{
                    color: 'error.main',
                    opacity: 0.7,
                    padding: '1px',
                    minWidth: '16px',
                    width: '16px',
                    height: '16px',
                    '&:hover': {
                      opacity: 1,
                      backgroundColor: 'error.main',
                      color: 'white'
                    }
                  }}
                >
                  <CloseIcon fontSize="small" sx={{ width: 10, height: 10 }} />
                </IconButton>
                
                <Chip
                  label={item.itemName || 'Select item'}
                  color="primary"
                  variant="outlined"
                  size="small"
                  onClick={() => handleEditItem(index)}
                  sx={{ 
                    cursor: 'pointer',
                    fontSize: '10px',
                    height: '20px',
                    '& .MuiChip-label': {
                      fontSize: '10px',
                      padding: '0 6px'
                    },
                    '&:hover': { backgroundColor: 'primary.main', color: 'white' }
                  }}
                />
              </div>
            ))}

            {/* Default "Other" handle в режиме редактирования */}
            <div style={{ 
              position: 'absolute',
              right: 25,
              top: `${40 + ((data.items?.length || 0) * 30)}px`,
              transform: 'translateY(-50%)',
              display: 'flex',
              alignItems: 'center',
              gap: '2px'
            }}>
              <Chip
                label="Other"
                color="secondary"
                variant="outlined"
                size="small"
                sx={{ 
                  cursor: 'default',
                  fontStyle: 'italic',
                  fontSize: '10px',
                  height: '20px',
                  '& .MuiChip-label': {
                    fontSize: '10px',
                    padding: '0 6px'
                  }
                }}
              />
            </div>

            {/* Default "None" handle в режиме редактирования */}
            <div style={{ 
              position: 'absolute',
              right: 25,
              top: `${40 + ((data.items?.length || 0) * 30 + 30)}px`,
              transform: 'translateY(-50%)',
              display: 'flex',
              alignItems: 'center',
              gap: '2px'
            }}>
              <Chip
                label="None"
                color="default"
                variant="outlined"
                size="small"
                sx={{ 
                  cursor: 'default',
                  fontStyle: 'italic',
                  fontSize: '10px',
                  height: '20px',
                  '& .MuiChip-label': {
                    fontSize: '10px',
                    padding: '0 6px'
                  }
                }}
              />
            </div>
          </>
        ) : (
          // Режим просмотра - показываем текстовые метки как в BranchNode
          <>
            {data.items?.map((item, index) => (
              <div key={index} style={{ 
                position: 'absolute',
                right: 25,
                top: `${40 + (index * 30)}px`,
                transform: 'translateY(-50%)',
                fontSize: '12px',
                color: '#666'
              }}>
                {item.itemName || 'Item'}
              </div>
            ))}

            {/* Default "Other" handle в режиме просмотра */}
            <div style={{
              position: 'absolute',
              right: 25,
              top: `${40 + ((data.items?.length || 0) * 30)}px`,
              transform: 'translateY(-50%)',
              fontSize: '12px',
              color: '#666',
              fontStyle: 'italic'
            }}>
              Other
            </div>

            {/* Default "None" handle в режиме просмотра */}
            <div style={{
              position: 'absolute',
              right: 25,
              top: `${40 + ((data.items?.length || 0) * 30 + 30)}px`,
              transform: 'translateY(-50%)',
              fontSize: '12px',
              color: '#666',
              fontStyle: 'italic'
            }}>
              None
            </div>
          </>
        )}
      </div>

      {/* Пины вынесены за пределы контейнера с padding */}
      {data.items?.map((item, index) => (
        <Handle
          key={`handle-item-${index}`}
          type="source"
          position={Position.Right}
          id={`item-${index}`}
          style={{
            right: 0,
            top: `${40 + (index * 30)}px`,
            transform: 'translateY(-50%)'
          }}
        />
      ))}

      {/* Default "Other" handle */}
      <Handle
        type="source"
        position={Position.Right}
        id="other"
        style={{
          right: 0,
          top: `${40 + ((data.items?.length || 0) * 30)}px`,
          transform: 'translateY(-50%)'
        }}
      />

      {/* Default "None" handle */}
      <Handle
        type="source"
        position={Position.Right}
        id="none"
        style={{
          right: 0,
          top: `${40 + ((data.items?.length || 0) * 30 + 30)}px`,
          transform: 'translateY(-50%)'
        }}
      />

      {/* Показываем кнопку Add Item под нодой как в DialogueNode */}
      {selected && (
        <div style={{ 
          position: 'absolute',
          bottom: -30,
          right: 0,
          zIndex: 1000
        }}>
          {(!data.items || data.items.length < 5) && (
            <Chip 
              icon={<AddRounded />}
              label="Item"
              clickable
              color="primary"
              size="small"
              sx={{ marginLeft: 0.5 }}
              onClick={handleAddItem}
            />
          )}
        </div>
      )}

      <ItemSelector
        open={isSelectorOpen}
        onClose={() => {
          setIsSelectorOpen(false);
          setActiveItemIndex(null);
        }}
        onSelect={handleItemSelect}
      />
    </div>
  );
}; 